import type { SortingState } from '@tanstack/react-table';
import type { ClaimModalMode } from '../stores/questClaimsStore';

/**
 * Pure (URL string) ⇆ (state) translation for the Quest Claims page. Mirrors
 * filters, sorting, the current page, and the open assign/edit modal into flat,
 * human-readable query params.
 */

export interface QuestClaimsFilters {
  seasonId: string;
  roundId: string;
  // 'all' | QuestClaimStatus
  status: string;
  appAndroidPackage: string;
  search: string;
}

export interface QuestClaimsUrlState {
  filters: QuestClaimsFilters;
  sorting: SortingState;
  page: number;
  // Only 'assign' and 'edit' round-trip through the URL; 'closed' clears them.
  modalMode: ClaimModalMode;
  selectedClaimId: string | null;
}

/** Every query-param key this page owns. Foreign params are preserved. */
export const QUEST_CLAIMS_URL_KEYS = [
  'season',
  'round',
  'app',
  'status',
  'search',
  'sort',
  'page',
  'modal',
  'claim',
] as const;

const DEFAULT_FILTERS: QuestClaimsFilters = {
  seasonId: '',
  roundId: '',
  status: 'all',
  appAndroidPackage: '',
  search: '',
};

const DEFAULT_SORTING: SortingState = [{ id: 'created', desc: true }];

const sortableColumns = new Set([
  'created',
  'revealedAt',
  'amountBaseUnits',
  'status',
  'walletId',
]);

const statusValues = new Set(['all', 'Unrevealed', 'Ready', 'Claimed']);

function parsePage(value: string | null): number {
  if (!value) {
    return 1;
  }
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return 1;
  }
  return parsed;
}

function parseSorting(value: string | null): SortingState {
  if (!value) {
    return DEFAULT_SORTING;
  }
  const [id, direction = 'asc'] = value.split(':');
  if (!id || !sortableColumns.has(id)) {
    return DEFAULT_SORTING;
  }
  return [{ id, desc: direction === 'desc' }];
}

export function parseQuestClaimsUrlState(
  params: URLSearchParams,
): QuestClaimsUrlState {
  const status = params.get('status');
  const modal = params.get('modal');
  const claim = params.get('claim');

  // 'edit' without a claim id can't open an editor, so fall back to closed.
  let modalMode: ClaimModalMode = 'closed';
  let selectedClaimId: string | null = null;
  if (modal === 'assign') {
    modalMode = 'assign';
  } else if (modal === 'edit' && claim) {
    modalMode = 'edit';
    selectedClaimId = claim;
  }

  return {
    filters: {
      seasonId: params.get('season') ?? '',
      roundId: params.get('round') ?? '',
      status: status && statusValues.has(status) ? status : 'all',
      appAndroidPackage: params.get('app') ?? '',
      search: params.get('search') ?? '',
    },
    sorting: parseSorting(params.get('sort')),
    page: parsePage(params.get('page')),
    modalMode,
    selectedClaimId,
  };
}

export function buildQuestClaimsParams(
  state: QuestClaimsUrlState,
): Record<string, string> {
  const params: Record<string, string> = {};
  const { filters } = state;

  if (filters.seasonId) {
    params.season = filters.seasonId;
  }
  if (filters.roundId) {
    params.round = filters.roundId;
  }
  if (filters.status && filters.status !== DEFAULT_FILTERS.status) {
    params.status = filters.status;
  }
  if (filters.appAndroidPackage) {
    params.app = filters.appAndroidPackage;
  }
  if (filters.search) {
    params.search = filters.search;
  }

  const sort = state.sorting[0];
  if (
    sort &&
    (sort.id !== DEFAULT_SORTING[0]?.id ||
      sort.desc !== DEFAULT_SORTING[0]?.desc)
  ) {
    params.sort = `${sort.id}:${sort.desc ? 'desc' : 'asc'}`;
  }

  if (state.page > 1) {
    params.page = String(state.page);
  }

  if (state.modalMode === 'assign') {
    params.modal = 'assign';
  } else if (state.modalMode === 'edit' && state.selectedClaimId) {
    params.modal = 'edit';
    params.claim = state.selectedClaimId;
  }

  return params;
}
